import { useEffect, useState, useRef } from "react";
import Sidebar from "../../components/Sidebar";
import "./AccountantDocument.css";
import {
  FaDownload,
  FaEye,
  FaCloudUploadAlt,
  FaSpinner,
  FaTimes,
  FaCheckCircle,
  FaSave,
  FaFileAlt,
  FaImage,
  FaFilePdf,
  FaReceipt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { screenPath } from "../../constant/strings";
import {
  getDocumentsListApi,
  getMediaSignedUrlApi,
  takeImageUploadExpenseApi,
  saveExpenseApi,
  createNewInvoiceApi,
  recordMileageApi,
  createBankStatementApi,
  categoryApi,
  getExpenseListApi,
  getSalesListApi,
  getMileageListApi,
  getBankStatementsListApi,
} from "../../services/apiCall";
import { useSelector } from "react-redux";
import CommonLoader from "../../components/CommonLoader";
import { toast } from "react-toastify";

export default function AccountantDocument() {
  const navigation = useNavigate();
  const businessId = useSelector((state) => state?.user?.businessId);
  const userId = useSelector((state) => state?.user?.userId);

  const [activeTab, setActiveTab] = useState("expense_receipt");
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  // Upload Data tab state
  const [uploadType, setUploadType] = useState("expense");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSavingUpload, setIsSavingUpload] = useState(false);
  const fileInputRef = useRef(null);

  // Extracted fields (dynamic per type)
  const [extracted, setExtracted] = useState(null);

  const tabs = [
    { id: "expense_receipt", label: "Expense" },
    { id: "sales_invoice", label: "Sales" },
    { id: "mileage_receipt", label: "Mileage" },
    { id: "bank_statement", label: "Bank" },
    { id: "misc", label: "Misc" },
    { id: "upload_data", label: "📤 Upload Data" },
  ];

  const uploadTypeOptions = [
    { value: "expense", label: "Expense", icon: "💸" },
    { value: "sale", label: "Sales Invoice", icon: "📄" },
    { value: "mileage", label: "Mileage", icon: "🚗" },
    { value: "bank", label: "Bank Statement", icon: "🏦" },
  ];

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await categoryApi();
        setCategories(res?.data?.expenseCategories ?? []);
      } catch (err) {}
    };
    loadCategories();
  }, []);

  const handleViewDocs = (doc) => {
    navigation(screenPath.documentViewerPage, {
      state: { document: { ...doc, activeTab } },
    });
  };

  const handleDownload = async (doc) => {
    try {
      const filename =
        doc.documentId ||
        doc.fileName ||
        doc.documentName ||
        doc.originalFileName ||
        doc.file_name;

      if (!filename) {
        const url = doc.downloadUrl || doc.url;
        if (url) window.open(url, "_blank");
        else toast.error("Document identifier or download link not available");
        return;
      }

      setIsLoading(true);
      const res = await getMediaSignedUrlApi(filename);
      console.log("resposne::::::::::", res);
      let signedUrl = res?.data;
      if (typeof signedUrl === "object" && signedUrl !== null) {
        signedUrl = signedUrl.url || signedUrl.signedUrl;
      }

      if (signedUrl) window.open(signedUrl, "_blank");
      else alert("Failed to get download URL from the server");
    } catch (error) {
      console.error("Failed to download document:", error);
      alert("Error downloading document");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!businessId) return;
    setIsLoading(true);
    try {
      let res;
      let docs = [];

      switch (activeTab) {
        case "expense_receipt":
          res = await getExpenseListApi({ businessId });
          docs = res?.data?.expenses || [];
          break;
        case "sales_invoice":
          res = await getSalesListApi({ businessId });
          docs = res?.data?.sales || [];
          break;
        case "mileage_receipt":
          res = await getMileageListApi({ businessId });
          docs = res?.data?.mileage || [];
          break;
        case "bank_statement":
          res = await getBankStatementsListApi({ businessId });
          docs = res?.data?.statements || [];
          break;
        case "misc":
          res = await getDocumentsListApi({
            businessId,
            documentType: "misc",
            limit: 100,
          });
          docs = res?.data?.data || res?.data?.documents || [];
          break;
        default:
          docs = [];
      }

      // Filter for items that have a valid documentId (or fileName for misc)
      const filteredDocs = docs.filter(
        (doc) =>
          doc.documentId ||
          doc.fileName ||
          doc.originalFileName ||
          doc.documentName,
      );

      console.log(`Fetched documents for ${activeTab}:`, filteredDocs);
      setDocuments(filteredDocs);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "upload_data") fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, activeTab]);

  // ── Upload Data Tab handlers ─────────────────────────────────────────

  const processFile = async (file) => {
    if (!file) return;
    setUploadFile(file);
    setUploadDone(false);
    setExtracted(null);

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => setUploadPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setUploadPreview(null);
    }

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("businessId", businessId);
    formData.append("type", uploadType);

    try {
      const response = await takeImageUploadExpenseApi(formData);
      const data = response?.data?.data;
      if (data) {
        setExtracted(data);
        setUploadDone(true);
        toast.success("File processed — data extracted!");
      } else {
        toast.info("File uploaded, please fill in the details below.");
        setExtracted({});
        setUploadDone(true);
      }
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error);
      toast.error("Failed to extract data from file.");
      setExtracted({});
      setUploadDone(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);

  const handleFieldChange = (key, value) => {
    setExtracted((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveExtracted = async () => {
    if (!extracted) {
      toast.error("No data to save.");
      return;
    }

    setIsSavingUpload(true);
    try {
      let response;

      if (uploadType === "expense") {
        const payload = {
          businessId,
          createdBy: userId,
          category: extracted.category || "",
          description: extracted.description || extracted.notes || "",
          gross: parseFloat(extracted.gross) || 0,
          vat: parseFloat(extracted.vat) || 0,
          net: parseFloat(extracted.net) || 0,
          transactionDate: extracted.createdAt
            ? new Date(extracted.createdAt).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0],
        };
        response = await saveExpenseApi(payload);
        if (response?.data?.expense) toast.success("Expense saved!");
        else toast.error("Failed to save expense.");
      }

      if (uploadType === "sale") {
        const payload = {
          businessId,
          createdBy: userId,
          invoiceNumber:
            extracted.invoiceNumber || `INV-${Date.now().toString().slice(-6)}`,
          gross: parseFloat(extracted.gross) || 0,
          vat: parseFloat(extracted.vat) || 0,
          net: parseFloat(extracted.net) || 0,
        };
        response = await createNewInvoiceApi(payload);
        if (response?.data) toast.success("Sales invoice saved!");
        else toast.error("Failed to save invoice.");
      }

      if (uploadType === "mileage") {
        const payload = {
          businessId,
          createdBy: userId,
          reason: extracted.reason || "",
          description: extracted.description || "",
          from: extracted.from || "",
          to: extracted.to || "",
          miles: parseFloat(extracted.miles) || 0,
        };
        response = await recordMileageApi(payload);
        if (response?.data?.mileage) toast.success("Mileage record saved!");
        else toast.error("Failed to save mileage.");
      }

      if (uploadType === "bank") {
        const payload = {
          businessId,
          createdBy: userId,
          transactionDate: extracted.createdAt
            ? new Date(extracted.createdAt).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0],
          referenceNumber:
            extracted.referenceNumber ||
            `REF-${Date.now().toString().slice(-6)}`,
          transactionType: extracted.transactionType || "CREDIT",
          amount: parseFloat(extracted.amount) || 0,
          balance: parseFloat(extracted.balance) || 0,
          category: extracted.category || "",
          description: extracted.description || "",
          accountId: "121212",
        };
        response = await createBankStatementApi(payload);
        if (response?.data?.statement) toast.success("Bank statement saved!");
        else toast.error("Failed to save bank statement.");
      }

      // Reset
      setUploadFile(null);
      setUploadPreview(null);
      setExtracted(null);
      setUploadDone(false);
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err?.message || "Failed to save. Please try again.");
    } finally {
      setIsSavingUpload(false);
    }
  };

  const getFileIcon = (file) => {
    if (!file) return <FaFileAlt />;
    if (file.type.startsWith("image/"))
      return <FaImage className="file-type-icon img" />;
    if (file.type === "application/pdf")
      return <FaFilePdf className="file-type-icon pdf" />;
    return <FaFileAlt className="file-type-icon doc" />;
  };

  const renderExtractedFields = () => {
    if (!extracted) return null;

    if (uploadType === "expense") {
      return (
        <div className="extracted-grid">
          <div className="ext-field">
            <label>Category</label>
            <select
              value={extracted.category || ""}
              onChange={(e) => handleFieldChange("category", e.target.value)}
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="ext-field">
            <label>Gross Amount (£)</label>
            <input
              type="number"
              placeholder="0.00"
              value={extracted.gross || ""}
              onChange={(e) => handleFieldChange("gross", e.target.value)}
            />
          </div>
          <div className="ext-field">
            <label>VAT (£)</label>
            <input
              type="number"
              placeholder="0.00"
              value={extracted.vat || ""}
              onChange={(e) => handleFieldChange("vat", e.target.value)}
            />
          </div>
          <div className="ext-field">
            <label>Net Amount (£)</label>
            <input
              type="number"
              placeholder="0.00"
              value={extracted.net || ""}
              onChange={(e) => handleFieldChange("net", e.target.value)}
            />
          </div>
          <div className="ext-field full">
            <label>Description / Notes</label>
            <textarea
              rows={2}
              placeholder="Description…"
              value={extracted.description || ""}
              onChange={(e) => handleFieldChange("description", e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (uploadType === "sale") {
      return (
        <div className="extracted-grid">
          <div className="ext-field">
            <label>Invoice Number</label>
            <input
              type="text"
              placeholder="INV-001"
              value={extracted.invoiceNumber || ""}
              onChange={(e) =>
                handleFieldChange("invoiceNumber", e.target.value)
              }
            />
          </div>
          <div className="ext-field">
            <label>Gross Amount (£)</label>
            <input
              type="number"
              placeholder="0.00"
              value={extracted.gross || ""}
              onChange={(e) => handleFieldChange("gross", e.target.value)}
            />
          </div>
          <div className="ext-field">
            <label>VAT (£)</label>
            <input
              type="number"
              placeholder="0.00"
              value={extracted.vat || ""}
              onChange={(e) => handleFieldChange("vat", e.target.value)}
            />
          </div>
          <div className="ext-field">
            <label>Net Amount (£)</label>
            <input
              type="number"
              placeholder="0.00"
              value={extracted.net || ""}
              onChange={(e) => handleFieldChange("net", e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (uploadType === "mileage") {
      return (
        <div className="extracted-grid">
          <div className="ext-field">
            <label>Reason for Travel</label>
            <input
              type="text"
              placeholder="e.g. Client Meeting"
              value={extracted.reason || ""}
              onChange={(e) => handleFieldChange("reason", e.target.value)}
            />
          </div>
          <div className="ext-field">
            <label>Miles</label>
            <input
              type="number"
              placeholder="0"
              value={extracted.miles || ""}
              onChange={(e) => handleFieldChange("miles", e.target.value)}
            />
          </div>
          <div className="ext-field">
            <label>From</label>
            <input
              type="text"
              placeholder="Start location"
              value={extracted.from || ""}
              onChange={(e) => handleFieldChange("from", e.target.value)}
            />
          </div>
          <div className="ext-field">
            <label>To</label>
            <input
              type="text"
              placeholder="Destination"
              value={extracted.to || ""}
              onChange={(e) => handleFieldChange("to", e.target.value)}
            />
          </div>
          <div className="ext-field full">
            <label>Description</label>
            <textarea
              rows={2}
              placeholder="Details…"
              value={extracted.description || ""}
              onChange={(e) => handleFieldChange("description", e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (uploadType === "bank") {
      return (
        <div className="extracted-grid">
          <div className="ext-field">
            <label>Reference Number</label>
            <input
              type="text"
              placeholder="REF12345"
              value={extracted.referenceNumber || ""}
              onChange={(e) =>
                handleFieldChange("referenceNumber", e.target.value)
              }
            />
          </div>
          <div className="ext-field">
            <label>Transaction Type</label>
            <select
              value={extracted.transactionType || ""}
              onChange={(e) =>
                handleFieldChange("transactionType", e.target.value)
              }
            >
              <option value="">Select type</option>
              <option value="CREDIT">CREDIT</option>
              <option value="DEBIT">DEBIT</option>
            </select>
          </div>
          <div className="ext-field">
            <label>Amount (£)</label>
            <input
              type="number"
              placeholder="0.00"
              value={extracted.amount || ""}
              onChange={(e) => handleFieldChange("amount", e.target.value)}
            />
          </div>
          <div className="ext-field">
            <label>Balance (£)</label>
            <input
              type="number"
              placeholder="0.00"
              value={extracted.balance || ""}
              onChange={(e) => handleFieldChange("balance", e.target.value)}
            />
          </div>
          <div className="ext-field">
            <label>Category</label>
            <input
              type="text"
              placeholder="e.g. Office Supplies"
              value={extracted.category || ""}
              onChange={(e) => handleFieldChange("category", e.target.value)}
            />
          </div>
          <div className="ext-field full">
            <label>Description</label>
            <textarea
              rows={2}
              placeholder="Description…"
              value={extracted.description || ""}
              onChange={(e) => handleFieldChange("description", e.target.value)}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="app">
      <Sidebar />
      <CommonLoader visible={isLoading} />
      <main className="main">
        <div className="doc-page-header">
          <div>
            <h1 className="doc-title">Accountant Documents</h1>
            <p className="doc-subtitle">
              All documents uploaded by your clients
            </p>
          </div>
        </div>

        <div className="clients-card">
          {/* Tabs */}
          <div className="tabs-container">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? "active" : ""} ${tab.id === "upload_data" ? "tab-upload" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ──── Upload Data Tab ──────────────────────────── */}
          {activeTab === "upload_data" ? (
            <div className="upload-data-container">
              <div className="upload-data-header">
                <h3>Upload & Extract Data</h3>
                <p>
                  Upload any file or image — we'll extract the data and let you
                  save it directly.
                </p>
              </div>

              {/* Type Selector */}
              <div className="upload-type-selector">
                {uploadTypeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    className={`type-option ${uploadType === opt.value ? "active" : ""}`}
                    onClick={() => {
                      setUploadType(opt.value);
                      setUploadFile(null);
                      setUploadPreview(null);
                      setExtracted(null);
                      setUploadDone(false);
                    }}
                  >
                    <span className="type-icon">{opt.icon}</span>
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>

              {/* Drop Zone */}
              <div
                className={`upload-drop-zone ${isDragging ? "dragging" : ""} ${uploadDone ? "done" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !uploadFile && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.csv,.xlsx,.xls,.doc,.docx"
                  style={{ display: "none" }}
                  onChange={handleFileInputChange}
                />

                {!uploadFile ? (
                  <>
                    <div className="drop-icon-wrap">
                      <FaCloudUploadAlt />
                    </div>
                    <h3>Drop your file here</h3>
                    <p>
                      or <span className="browse-link">browse to upload</span>
                    </p>
                    <span className="drop-hint">
                      Accepts: Images, PDF, CSV, XLSX, DOC
                    </span>
                  </>
                ) : (
                  <div className="upload-file-status">
                    <div className="file-info-row">
                      {getFileIcon(uploadFile)}
                      <div className="file-meta">
                        <span className="file-name">{uploadFile.name}</span>
                        <span className="file-size">
                          {(uploadFile.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                      <button
                        className="file-remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadFile(null);
                          setUploadPreview(null);
                          setExtracted(null);
                          setUploadDone(false);
                        }}
                      >
                        <FaTimes />
                      </button>
                    </div>

                    {isUploading && (
                      <div className="processing-indicator">
                        <FaSpinner className="spin" />
                        <span>Extracting data from your file…</span>
                      </div>
                    )}

                    {uploadDone && !isUploading && (
                      <div className="processing-done">
                        <FaCheckCircle />
                        <span>Data extracted successfully</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Preview for images */}
              {uploadPreview && (
                <div className="upload-img-preview">
                  <img src={uploadPreview} alt="Document preview" />
                </div>
              )}

              {/* Extracted Data Form */}
              {uploadDone && extracted !== null && (
                <div className="extracted-data-card">
                  <div className="extracted-data-header">
                    <FaReceipt />
                    <h4>Extracted Data — Review & Edit</h4>
                  </div>

                  {renderExtractedFields()}

                  <div className="extracted-save-row">
                    <button
                      className="btn-save-extracted"
                      onClick={handleSaveExtracted}
                      disabled={isSavingUpload}
                    >
                      {isSavingUpload ? (
                        <>
                          <FaSpinner className="spin" /> Saving…
                        </>
                      ) : (
                        <>
                          <FaSave /> Save{" "}
                          {
                            uploadTypeOptions.find(
                              (o) => o.value === uploadType,
                            )?.label
                          }
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ──── Normal Document List ──────────────────────── */
            <>
              <h3 className="clients-title">
                {tabs.find((t) => t.id === activeTab)?.label} Documents
              </h3>
              <p className="clients-subtitle">
                View and manage specific documents
              </p>

              {!isLoading && documents.length === 0 ? (
                <div className="empty-state">
                  <FaFileAlt className="empty-icon" />
                  <p>No documents found in this category.</p>
                </div>
              ) : (
                <table className="clients-table">
                  <thead>
                    <tr>
                      <th>File Name</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Uploaded</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc, index) => (
                      <tr key={doc.id || index}>
                        <td className="name">
                          {doc.documentId ? (
                            <span className="doc-id-label">
                              {activeTab
                                .replace(/_/g, " ")
                                .charAt(0)
                                .toUpperCase() +
                                activeTab
                                  .replace(/_/g, " ")
                                  .slice(1)
                                  .replace(" receipt", "")
                                  .replace(" invoice", "")}{" "}
                              - {doc.id.slice(-6).toUpperCase()}
                            </span>
                          ) : (
                            doc.fileName ||
                            doc.documentName ||
                            doc.originalFileName ||
                            `Document ${index + 1}`
                          )}
                        </td>
                        <td>{doc.documentType || doc.type || activeTab}</td>
                        <td>
                          <span
                            className={`status-badge ${(doc.status || "processed").toLowerCase().includes("process") || doc.status === "Active" ? "active" : "pending"}`}
                          >
                            {doc.status || "Processed"}
                          </span>
                        </td>
                        <td>
                          {doc.createdAt
                            ? new Date(doc.createdAt).toLocaleDateString()
                            : doc.uploaded || "N/A"}
                        </td>
                        <td className="actions">
                          <FaEye
                            size={17}
                            color="#4f46e5"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleViewDocs(doc)}
                          />
                          <div style={{ width: 8 }} />
                          <FaDownload
                            size={17}
                            color="#4f46e5"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDownload(doc)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
